import { Component, computed, inject, signal, WritableSignal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { ApiService } from '../../api.service';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { TAGImageCategory } from '../../../models/tagimagecategory';
import { TAGEvent } from '../../../models/tagevent';
import { FieldsetModule } from 'primeng/fieldset';
import { TAGImage } from '../../../models/tagimage';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { FluidModule } from 'primeng/fluid';
import { UseHttpImageSourcePipe } from '../../authimage.pipe';
import { TextareaModule } from 'primeng/textarea';
import {
  newProgressDialogStep,
  ProgressDialogStep,
  ProgressDialogStepState,
  openProgressDialog,
} from '../../components/progressDialog/progressDialog';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';

type LocalImageData = TAGImage & {
  imageData: string;
  form: FormGroup;
  localID: string;
  imageLoading: boolean;
};

@Component({
  selector: 'app-internal-gallery',
  imports: [
    InternalToolbar,
    TranslatePipe,
    SkeletonModule,
    TableModule,
    ButtonModule,
    MatIcon,
    FieldsetModule,
    ToggleButtonModule,
    FloatLabelModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    AutoCompleteModule,
    ReactiveFormsModule,
    FluidModule,
    UseHttpImageSourcePipe,
    TextareaModule,
    FileUploadModule,
    ReactiveIconTextButton,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-gallery.html',
  styleUrl: './internal-gallery.scss',
})
export class InternalGallery {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  //private route = inject(ActivatedRoute);
  public api = inject(ApiService);

  categories = signal<TAGImageCategory[]>([]);
  loadingCategories = signal(true);
  selectedCategory = signal<TAGImageCategory>(undefined);

  searchFilter = signal('');
  filteredCategories = computed(() => {
    return this.filterCategories(this.categories(), this.searchFilter());
  });

  categoryImages = signal<LocalImageData[]>([]);
  loadingImages = signal(true);

  createdImages = signal<LocalImageData[]>([]);
  removedImages = signal<string[]>([]);
  categoryFormTouched = signal(false);
  imageFormTouched = signal(false);
  imagesChanged = computed(() => {
    if (this.createdImages().length > 0) return true;
    if (this.removedImages().length > 0) return true;

    if (this.imageFormTouched()) return true;
    if (this.categoryFormTouched()) return true;

    for (let image of this.categoryImages()) {
      if (image.imageData !== undefined) return true;
      if (image.form.touched) return true;
    }

    return this.categoryForm.touched;
  });

  events = signal<TAGEvent[]>([]);
  loadingEvents = signal(true);
  filteredEvents = signal<TAGEvent[]>([]);
  eventControl = new FormControl<TAGEvent>(undefined);

  categoryForm = new FormGroup({
    isPublic: new FormControl(false, [Validators.required]),
    title: new FormControl('', [Validators.required]),
    description: new FormControl('', []),
  });

  images = computed(() => {
    let allImages = [...this.categoryImages(), ...this.createdImages()];

    for (let removeImage of this.removedImages()) {
      let index = allImages.findIndex(
        (i) => i._id !== undefined && removeImage !== undefined && i._id === removeImage,
      );
      if (index !== -1) allImages.splice(index, 1);
    }

    allImages.sort((a: TAGImage, b: TAGImage) => {
      return a.uploaddate.getTime() - b.uploaddate.getTime();
    });

    return allImages;
  });

  filterCategories(allCategories: TAGImageCategory[], filter: string) {
    if (filter.trim() == '') {
      return allCategories;
    }

    return allCategories.filter((t) => {
      return (
        t.alias.toLowerCase().includes(filter.toLowerCase()) ||
        t.title.toLowerCase().includes(filter.toLowerCase()) ||
        t.description.toLowerCase().includes(filter.toLowerCase()) ||
        (this.getEvent(t.eventid)?.name ?? '').toLowerCase().includes(filter.toLowerCase())
      );
    });
  }

  getEvent(eventid: string) {
    return this.events().find((e) => e._id === eventid);
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.loadImageCategories();
      this.api
        .getEvents()
        .then((events: TAGEvent[]) => {
          this.events.set(events);
          this.loadingEvents.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'danger',
            summary: this.translate.translate("errors.couldNotLoadEvents")(),
            detail: reason.message,
          });
        });
    });
  }

  getImageLink(image: LocalImageData) {
    if (image.imageData) return image.imageData;
    else if (image._id) return this.api.getImageLink(image._id);
    else {
      console.error('Trying to load imagedata of new image, but is not available');
      return 'error';
    }
  }

  getImageThumbLink(image: LocalImageData) {
    if (image.imageData) return image.imageData;
    else if (image._id) return this.api.getThumbLink(image._id);
    else {
      console.error('Trying to load imagedata of new image, but is not available');
      return 'error';
    }
  }

  _searchEvent(event: AutoCompleteCompleteEvent) {
    this.filteredEvents.set(
      this.events().filter((evt) => evt.name.toLowerCase().includes(event.query.toLowerCase())),
    );

    console.log(event.query, this.filteredEvents());
  }

  createImageFormGroup() {
    return new FormGroup({
      //_id: new FormControl('ERROR', []),
      title: new FormControl('', [Validators.required]),
      description: new FormControl('', []),
      isPublic: new FormControl(false, []),
      watermark: new FormControl(false, []),
    });
  }

  loadImageCategories() {
    this.loadingCategories.set(true);
    this.categories.set(Array.from({ length: 3 }).map((_, i) => new TAGImageCategory()));

    this.api
      .getImageCategories()
      .then((categories: TAGImageCategory[]) => {
        categories.sort((a, b) => b.createdate.getTime() - a.createdate.getTime());
        this.categories.set(categories);
        this.loadingCategories.set(false);

        let lastSelectedCategory = this.selectedCategory();
        if (lastSelectedCategory != undefined) {
          console.log(
            'Reselect category',
            categories.find((c) => c._id === lastSelectedCategory._id),
          );
          this.selectCategory(categories.find((c) => c._id === lastSelectedCategory._id));
        }
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate("errors.couldNotLoadImageCategories")(),
          detail: reason.message,
        });
      });
  }

  loadImages() {
    if (this.selectedCategory()._id === undefined) {
      this.loadingImages.set(false);
      return;
    }

    this.loadingImages.set(true);

    if (this.selectedCategory() !== undefined) {
      this.api
        .getImages(this.selectedCategory()._id)
        .then((images: TAGImage[]) => {
          images.sort((a: TAGImage, b: TAGImage) => {
            return a.uploaddate.getTime() - b.uploaddate.getTime();
          });

          this.categoryImages.set(
            images.map((i) => {
              let localImage = i as LocalImageData;
              localImage.imageData = undefined;
              localImage.form = this.createImageFormGroup();
              localImage.form.patchValue(i);
              localImage.localID = this.randomString();
              localImage.imageLoading = true;

              return localImage;
            }),
          );
          this.loadingImages.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate("errors.couldNotLoadImages")(),
            detail: reason.message,
          });
        });
    } else {
      this.categoryImages.set([]);
      this.createdImages.set([]);
      this.removedImages.set([]);
      this.loadingImages.set(false);
    }
  }

  setImageLoading(image: LocalImageData, loading: boolean) {
    this.categoryImages.update((i) => {
      let index = i.findIndex((i2) => i2._id === image._id);
      i[index].imageLoading = loading;
      return [...i];
    });
  }

  selectCategory(category: TAGImageCategory) {
    //if (this.selectedCategory()?._id === category._id) return;

    let catCopy = undefined;
    if (category) {
      catCopy = new TAGImageCategory(category.serialize());
    }
    this.selectedCategory.set(catCopy);

    if (catCopy !== undefined) {
      this.categoryImages.set([]);
      this.createdImages.set([]);
      this.removedImages.set([]);
      this.imageFormTouched.set(false);
      this.categoryFormTouched.set(false);

      this.categoryForm.patchValue(catCopy);
      this.categoryForm.markAsUntouched();
      if (catCopy) {
        this.eventControl.setValue(this.getEvent(catCopy.eventid));
      }
      this.loadImages();
    }
  }

  createCategory() {
    this.selectCategory(new TAGImageCategory({ title: 'New Category' }));
  }

  addNewImage(event: FileUploadHandlerEvent) {
    let currentIndex = 0;

    const reader = new FileReader();
    reader.onloadend = () => {
      let newImg = new TAGImage() as LocalImageData;

      newImg.imageData = reader.result as string;
      newImg.title = 'New Image';
      newImg.form = this.createImageFormGroup();
      newImg.form.patchValue(newImg);
      newImg.localID = this.randomString();

      this.createdImages.update((i) => [...i, newImg]);

      currentIndex++;
      if (event.files.length > currentIndex) {
        reader.readAsDataURL(event.files[currentIndex]);
      }
    };

    reader.readAsDataURL(event.files[0]);
  }

  clickReupload(image: LocalImageData) {
    document.getElementById('fileInput-' + image.localID).click();
  }

  reuploadImage(image: LocalImageData, files: FileList) {
    console.log('Reupload', files);
    const reader = new FileReader();
    reader.onloadend = () => {
      let newImg = new TAGImage(image.serialize()) as LocalImageData;

      newImg.imageData = reader.result as string;
      newImg.form = image.form;
      newImg.localID = image.localID;

      if (newImg._id !== undefined) {
        this.categoryImages.update((i) => {
          let index = i.findIndex((i2) => i2._id === newImg._id);
          i.splice(index, 1, newImg);
          return [...i];
        });
      } else {
        this.createdImages.update((i) => {
          let index = i.findIndex((i2) => i2.localID === newImg.localID);
          i.splice(index, 1, newImg);
          return [...i];
        });
      }
    };

    reader.readAsDataURL(files[0]);
  }

  removeImage(image: LocalImageData) {
    if (image._id === undefined) {
      this.createdImages.update((i) => {
        let index = i.findIndex((im) => im == image);
        if (index !== -1) i.splice(index, 1);
        else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate("errors.couldNotDeleteNewImage")(),
            detail: 'ID: ' + image._id + ' Index: ' + index,
          });
        }
        return [...i];
      });
    } else {
      this.removedImages.update((r) => {
        if (!r.includes(image._id)) r.push(image._id);
        return [...r];
      });
    }
  }

  deleteCategory() {
    if (this.selectedCategory()._id === undefined) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.gallery.deleteTitle', {
          category: this.selectedCategory(),
        })(),
        this.translate.translate('internal.gallery.deleteMessage', {
          category: this.selectedCategory(),
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.gallery.deleteProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteImageCategory(this.selectedCategory()._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            this.loadImageCategories();
            await pDialog.finishDialog();
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  cancelEdit() {
    if (this.selectedCategory()._id === undefined) {
      return this.selectCategory(undefined);
    }

    let originalCategory = this.categories().find((c) => c._id === this.selectedCategory()._id);
    this.selectCategory(originalCategory);
    /* this.selectedCategory.set(originalCategory);

    this.createdImages.set([]);
    this.removedImages.set([]);
    this.imageFormTouched.set(false);

    this.loadImages(); */
  }

  saveEdit() {
    if (this.categoryForm.invalid) return;
    for (let image of this.images()) {
      if (image.form.invalid) return;
    }

    let categoryIsNew = this.selectedCategory()._id === undefined;

    let updateCategoryStep = newProgressDialogStep(
      new ProgressDialogStep(
        ProgressDialogStepState.WAITING,
        this.translate.translate('internal.gallery.updateCategoryProgress')(),
      ),
    );

    if (categoryIsNew) {
      updateCategoryStep = newProgressDialogStep(
        new ProgressDialogStep(
          ProgressDialogStepState.WAITING,
          this.translate.translate('internal.gallery.createCategoryProgress')(),
        ),
      );
    }

    let imageUploadStep = newProgressDialogStep(
      new ProgressDialogStep(
        ProgressDialogStepState.WAITING,
        this.translate.translate('internal.gallery.imageUploadProgress')(),
      )
        .setShowProgress(true)
        .setProgress(0),
    );

    let imageProcessingStep = newProgressDialogStep(
      new ProgressDialogStep(
        ProgressDialogStepState.WAITING,
        this.translate.translate('internal.gallery.imageProcessingProgress')(),
      )
        .setShowProgress(true)
        .setProgress(0),
    );

    let progressDialog = openProgressDialog(this.dialogService, [
      updateCategoryStep,
      imageUploadStep,
      imageProcessingStep,
    ]);

    progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
      let newImages = this.createdImages();
      let reuploadedImages = [];
      let changedImages = [];
      for (let image of this.categoryImages()) {
        if (this.removedImages().includes(image._id)) continue;
        if (image._id === undefined) {
          console.error("Image ID shouldn't be undefined");
          continue;
        }

        if (image.imageData !== undefined) reuploadedImages.push(image);
        else if (image.form.touched) changedImages.push(image);
      }

      if (newImages.length == 0 && reuploadedImages.length == 0) {
        imageUploadStep.update((s) => s.setState(ProgressDialogStepState.SKIPPED));

        if (changedImages.length === 0)
          imageProcessingStep.update((s) => s.setState(ProgressDialogStepState.SKIPPED));
      }

      let category = this.selectedCategory();
      category.deserialize(this.categoryForm.value);
      category.eventid = this.eventControl.value?._id ?? "";

      if (categoryIsNew) {
        this.api
          .newImageCategory(category)
          .then((newCategoryId: string) => {
            this.selectedCategory.update((c) => {
              let newCat = new TAGImageCategory(category.serialize());
              newCat._id = newCategoryId;
              return newCat;
            });

            updateCategoryStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            if ((newImages.length > 0 || reuploadedImages.length > 0) || changedImages.length > 0) {
              this.updateImages(
                newCategoryId,
                newImages,
                reuploadedImages,
                changedImages,
                this.removedImages(),
                imageUploadStep,
                imageProcessingStep,
              )
                .then(() => {
                  this.loadImageCategories();
                  pDialog.finishDialog();
                })
                .catch((reason) => {
                  pDialog.finishDialog(-1);
                  console.log(reason);
                });
            } else {
              this.loadImageCategories();
              pDialog.finishDialog();
            }
          })
          .catch((reason) => {
            updateCategoryStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateCategoryStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .updateImageCategory(category)
          .then((categoryId: string) => {
            updateCategoryStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));

            if ((newImages.length > 0 || reuploadedImages.length > 0) || changedImages.length > 0) {
              this.updateImages(
                categoryId,
                newImages,
                reuploadedImages,
                changedImages,
                this.removedImages(),
                imageUploadStep,
                imageProcessingStep,
              )
                .then(() => {
                  this.loadImageCategories();
                  pDialog.finishDialog();
                })
                .catch((reason) => {
                  pDialog.finishDialog(-1);
                  console.log(reason);
                });
            } else {
              this.loadImageCategories();
              pDialog.finishDialog();
            }
          })
          .catch((reason) => {
            updateCategoryStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateCategoryStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }

  updateImages(
    categoryId: string,
    newImages: LocalImageData[],
    reuploadedImages: LocalImageData[],
    updatedImages: LocalImageData[],
    removedImages: string[],
    imageUploadStep: WritableSignal<ProgressDialogStep>,
    imageProcessingStep: WritableSignal<ProgressDialogStep>,
  ) {
    return new Promise<void>(async (resolve, reject) => {
      let totalUploads = newImages.length + reuploadedImages.length;
      let totalProcessing =
        newImages.length + reuploadedImages.length + updatedImages.length + removedImages.length;

      imageUploadStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));
      imageProcessingStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      let doneUploads = 0;
      let doneProcessing = 0;

      let uploadErrors = [];
      let processingErrors = [];

      // Upload new images
      for (let newImage of newImages) {
        try {
          newImage.categoryid = categoryId;
          newImage.deserialize(newImage.form.value);

          let newImageId = await this.api.newImage(newImage, newImage.imageData, (p) => {
            console.log(doneUploads, totalUploads, (doneUploads / totalUploads) * 100, p);
            imageUploadStep.update((s) =>
              s.setProgress((doneUploads / totalUploads) + p / totalUploads),
            );
          });

          doneUploads++;
          doneProcessing++;
          imageProcessingStep.update((s) =>
            s.setProgress(doneProcessing / totalProcessing),
          );
        } catch (reason) {
          uploadErrors.push(this.translate.translate("errors.imageUploadErrors.newImage", reason)());
        }
      }

      // Update reuploaded images
      for (let reuploadedImage of reuploadedImages) {
        try {
          reuploadedImage.deserialize(reuploadedImage.form.value);

          let imageId = await this.api.updateImage(
            reuploadedImage,
            reuploadedImage.imageData,
            (p) => {
              imageUploadStep.update((s) =>
                s.setProgress(doneUploads / totalUploads + p / totalUploads),
              );
            },
          );

          doneUploads++;
          doneProcessing++;
          imageProcessingStep.update((s) =>
            s.setProgress(doneProcessing / totalProcessing),
          );
        } catch (reason) {
          uploadErrors.push(this.translate.translate("errors.imageUploadErrors.reuploadedImage", reason)());
        }
      }

      if (uploadErrors.length > 0) {
        imageUploadStep.update((s) =>
          s
            .setProgress(1)
            .setState(ProgressDialogStepState.ERROR)
            .setSubtitle(uploadErrors.join('\n')),
        );
      } else {
        imageUploadStep.update((s) => s.setProgress(1).setState(ProgressDialogStepState.SUCCESS));
      }

      // Update non-reuploaded images
      for (let updatedImage of updatedImages) {
        try {
          updatedImage.deserialize(updatedImage.form.value);

          let imageId = await this.api.updateImage(updatedImage);

          doneProcessing++;
          imageProcessingStep.update((s) =>
            s.setProgress(doneProcessing / totalProcessing),
          );
        } catch (reason) {
          processingErrors.push(this.translate.translate("errors.imageUploadErrors.updatedImage", reason)());
        }
      }

      // Remove removed images
      for (let removedImage of removedImages) {
        try {
          let imageId = await this.api.deleteImage(removedImage);

          doneProcessing++;
          imageProcessingStep.update((s) =>
            s.setProgress(doneProcessing / totalProcessing),
          );
        } catch (reason) {
          processingErrors.push(this.translate.translate("errors.imageUploadErrors.removedImage", reason)());
        }
      }

      if (processingErrors.length > 0) {
        imageProcessingStep.update((s) =>
          s
            .setProgress(1)
            .setState(ProgressDialogStepState.ERROR)
            .setSubtitle(processingErrors.join('\n')),
        );
      } else {
        imageProcessingStep.update((s) =>
          s.setProgress(1).setState(ProgressDialogStepState.SUCCESS),
        );
      }

      let allErrors = uploadErrors.concat(processingErrors);

      if (allErrors.length > 0) {
        reject(allErrors.join("\n"));
      } else {
        resolve();
      }
    });
  }

  randomString() {
    let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890';
    let lengthOfCode = 20;
    let text = '';
    for (let i = 0; i < lengthOfCode; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }
}
